import * as React from "react";
import { useReactQueryClient } from "../../../Common";
import { DataSource_MyBag_Totals_QueryKey } from "./data-source--mybag-totals-query-key-factory";

import {
  DataSourceMyBagTotalsQueryProvider,
  DataSourceMyBagTotalsQueryProviderProps,
} from "./data-source--mybag-totals-query-provider";

export interface DataSourceMyBagTotalsControllerConsumerInterface {
  request: (desiredCart: DataSourceMyBagTotalsQueryProviderProps) => void;
  enablePoolingInvalidations: () => void;
  flushPooledInvalidations: () => void;
  invalidate: () => void;
}

const defaultValue =
  {} as unknown as DataSourceMyBagTotalsControllerConsumerInterface;

const DataSourceMyBagTotalsControllerContext =
  React.createContext(defaultValue);

export const DataSourceMyBagTotalsControllerProvider: React.FunctionComponent =
  (props) => {
    const queryClient = useReactQueryClient();

    const [desiredCart, setDesiredCart] =
      React.useState<DataSourceMyBagTotalsQueryProviderProps | null>();

    const [amIPoolingInvalidations, setAmIPoolingInvalidations] =
      React.useState(false);
    const [needsToInvalidate, setNeedsToInvalidate] = React.useState(false);

    const flush: DataSourceMyBagTotalsControllerConsumerInterface["flushPooledInvalidations"] =
      React.useCallback(() => {
        setAmIPoolingInvalidations(false);
        queryClient.invalidateQueries(
          DataSource_MyBag_Totals_QueryKey.mybagTotalsInvalidationKey()
        );
        setNeedsToInvalidate(false);
      }, []);
    React.useEffect(() => {
      if (needsToInvalidate && !amIPoolingInvalidations) {
        flush();
      }
    }, [needsToInvalidate, amIPoolingInvalidations, flush]);

    const request: DataSourceMyBagTotalsControllerConsumerInterface["request"] =
      React.useCallback((desiredCart) => {
        setDesiredCart(desiredCart);
      }, []);

    const enablePoolingInvalidations: DataSourceMyBagTotalsControllerConsumerInterface["enablePoolingInvalidations"] =
      React.useCallback(() => {
        setAmIPoolingInvalidations(true);
      }, []);
    const invalidate: DataSourceMyBagTotalsControllerConsumerInterface["invalidate"] =
      React.useCallback(() => {
        setNeedsToInvalidate(true);
      }, []);
    const flushPooledInvalidations: DataSourceMyBagTotalsControllerConsumerInterface["flushPooledInvalidations"] =
      React.useCallback(() => {
        setAmIPoolingInvalidations(false);
      }, []);

    const value: DataSourceMyBagTotalsControllerConsumerInterface =
      React.useMemo(
        () => ({
          request,
          enablePoolingInvalidations,
          flushPooledInvalidations,
          invalidate,
        }),
        [
          request,
          enablePoolingInvalidations,
          flushPooledInvalidations,
          invalidate,
        ]
      );

    return (
      <DataSourceMyBagTotalsControllerContext.Provider value={value}>
        {desiredCart ? (
          <DataSourceMyBagTotalsQueryProvider
            isCustomerLoggedIn={desiredCart.isCustomerLoggedIn}
            cartId={desiredCart.cartId}
            useInitialState={desiredCart.useInitialState}
          >
            {props?.children || []}
          </DataSourceMyBagTotalsQueryProvider>
        ) : (
          <React.Fragment>{props?.children || []}</React.Fragment>
        )}
      </DataSourceMyBagTotalsControllerContext.Provider>
    );
  };

export const useDataSourceMyBagTotalsController = () => {
  const context = React.useContext(DataSourceMyBagTotalsControllerContext);
  if (context === defaultValue) {
    throw new Error(
      "useDataSourceMyBagTotalsController must be used within a DataSourceMyBagTotalsControllerProvider"
    );
  }
  return context;
};
